/**
 * Small accessibility fixes for things that come from the theme's built-in carousels, Shopify and apps.
 * Loaded from layout/theme.liquid on every page. It only touches attributes, never how anything looks.
 *
 * 1. Carousel slides that are scrolled out of view are hidden from screen readers (aria-hidden="true"), but their
 *    links and buttons could still be reached with the Tab key, which confuses screen reader users. Those links and
 *    buttons are taken out of the Tab order while their slide is out of view, and put back when it scrolls in.
 *    Taps and clicks work exactly as before.
 * 2. Frames added by apps and Shopify (the Google badge, analytics pixels, the preview bar) get a title, so screen
 *    readers can say what they are.
 * 3. The greyed-out "previous/next page" arrow in collection pagination is a plain <span> with a label; it gets the
 *    role of a disabled link so the label is valid.
 * 4. The grey loading placeholders in product recommendations get an image role so their "loading" label is valid.
 */

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), summary';
const PARKED = 'data-a11y-tabindex';

/** @param {Element} slide */
function syncSlide(slide) {
  const hidden = slide.getAttribute('aria-hidden') === 'true';
  if (hidden) {
    for (const element of slide.querySelectorAll(FOCUSABLE)) {
      if (element.hasAttribute(PARKED)) continue;
      element.setAttribute(PARKED, element.getAttribute('tabindex') ?? '');
      element.setAttribute('tabindex', '-1');
    }
  } else {
    for (const element of slide.querySelectorAll(`[${PARKED}]`)) {
      const original = element.getAttribute(PARKED);
      if (original) element.setAttribute('tabindex', original);
      else element.removeAttribute('tabindex');
      element.removeAttribute(PARKED);
    }
  }
}

const FRAME_TITLES = [
  [/merchantwidget/i, 'Google customer reviews'],
  [/web-pixel/i, 'Shopify analytics'],
  [/PBarNextFrame|preview-bar/i, 'Shopify theme preview bar'],
];

/** @param {HTMLIFrameElement} frame */
function titleFrame(frame) {
  if (frame.hasAttribute('title')) return;
  const key = `${frame.id} ${frame.name} ${frame.src}`;
  const match = FRAME_TITLES.find(([pattern]) => pattern.test(key));
  frame.setAttribute('title', match ? match[1] : 'Embedded content');
}

/** @param {ParentNode} root */
function fixStatic(root) {
  root.querySelectorAll('span.pagination__link--disabled[aria-label]:not([role])').forEach((element) => {
    element.setAttribute('role', 'link');
    element.setAttribute('aria-disabled', 'true');
  });
  root.querySelectorAll('.product-recommendations__skeleton-item[aria-label]:not([role])').forEach((element) => {
    element.setAttribute('role', 'img');
  });
  root.querySelectorAll('iframe:not([title])').forEach((frame) => titleFrame(/** @type {HTMLIFrameElement} */ (frame)));
  root.querySelectorAll('slideshow-slide[aria-hidden="true"]').forEach(syncSlide);
}

fixStatic(document);

new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'attributes') {
      if (record.target instanceof Element && record.target.localName === 'slideshow-slide') syncSlide(record.target);
      continue;
    }
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node instanceof HTMLIFrameElement) titleFrame(node);
      else if (node.firstElementChild) fixStatic(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
